import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Photo from '../../components/album/photo';
import MatchCentre from '../../components/match-centre';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import { fetchPageData } from '../../store/teams/match-centre/actions';
import {
  getPageData,
  getPageLoadingError,
  isLoadingPageData,
  hasLoadedPageData,
} from '../../store/teams/match-centre/selectors';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import TEAM_PROP_TYPES from '../../lib/prop-types/team';
import LOADING_ERROR_PROP_TYPES from '../../lib/prop-types/loading-error';
import withTeam from '../../lib/with-team';
import { availableSections } from '../../components/match-centre/constants';
import { getImage, nextImage, prevImage } from '../../store/media/albums/selectors';

class MatchCentrePage extends React.Component {
  static async getInitialProps({ asPath, club, query, isServer, reduxStore, req, res }) {
    const teamId = parseInt(query.teamId, 10);
    const albumId = query.albumId ? parseInt(query.albumId, 10) : null;
    const imageId = query.imageId ? parseInt(query.imageId, 10) : null;
    const { fixtureId } = query;

    let frameless = false;
    let section;
    const photoRegex = /photos\/([0-9]+)-([0-9]+)$/;
    if (photoRegex.exec(asPath)) {
      section = 'photo';

      frameless = true;
    } else {
      [section] = asPath
        .replace(/\/$/, '')
        .substring(asPath.replace(/\/$/, '').lastIndexOf('/') + 1)
        .split(/[?#]/);
      if (!section || section === fixtureId || !availableSections.includes(section)) {
        section = 'overview';
      }
    }

    if (isServer && club) {
      const { error, accessLevel } = await reduxStore.dispatch(
        fetchPageData(club.id, teamId, fixtureId, req),
      );
      if (error && error.errorStatusCode && res) {
        res.statusCode = error.errorStatusCode;
      }

      if (accessLevel && accessLevel !== 'public') {
        // Do not cache the HTML output of non-public pages
        res.doNotCache = true;
      }
    }

    return {
      activeSection: 'teams',
      activeSubSection: 'fixtures',
      albumId,
      teamId,
      fixtureId,
      frameless,
      imageId,
      namespacesRequired: ['common', 'teams'],
      section,
      pageType: `team.match_centre.${section}`,
      pageUid: `team.match_centre.${section}.${fixtureId}`,
    };
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { teamId: prevTeamId, fixtureId: prevFixtureId } = prevProps;
    const { teamId, fixtureId } = this.props;
    if (prevTeamId !== teamId || prevFixtureId !== fixtureId) {
      this.loadIfRequired();
    }
  }

  loadIfRequired() {
    const { isLoading, previouslyLoaded } = this.props;
    if (!isLoading && !previouslyLoaded) {
      const {
        club: { id: clubId },
        teamId,
        fixtureId,
        loadPageData,
      } = this.props;
      loadPageData(clubId, teamId, fixtureId);
    }
  }

  render() {
    const { club, fixtureId, loadingError, section, teamId } = this.props;
    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    if (section === 'photo') {
      const { image, isLoading, nextImg, pageData, prevImg } = this.props;
      if (!image) {
        return <>not found</>;
      }
      const { album } = pageData;
      return (
        <Photo
          album={album}
          club={club}
          image={image}
          isLoading={isLoading}
          nextImage={nextImg}
          prevImage={prevImg}
          title={image.title}
          teamId={teamId}
          fixtureId={fixtureId}
        />
      );
    }

    const { team, isLoading, pageData } = this.props;
    // If we're trying to go to the report, but don't have the data, go to overview
    let activeSection = section;
    if (activeSection === 'report' && !pageData.report) {
      activeSection = 'overview';
    }
    return (
      <MatchCentre
        isLoading={isLoading}
        club={club}
        clubId={club.id}
        {...pageData}
        team={team}
        teamId={teamId}
        fixtureId={fixtureId}
        activeSection={activeSection || 'overview'}
      />
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  image: getImage(state, ownProps.albumId, ownProps.imageId),
  nextImg: nextImage(state, ownProps.albumId, ownProps.imageId),
  prevImg: prevImage(state, ownProps.albumId, ownProps.imageId),
  loadingError: getPageLoadingError(state, ownProps.teamId, ownProps.fixtureId),
  pageData: getPageData(state, ownProps.teamId, ownProps.fixtureId),
  isLoading: isLoadingPageData(state, ownProps.teamId, ownProps.fixtureId),
  previouslyLoaded: hasLoadedPageData(state, ownProps.teamId, ownProps.fixtureId),
});

const dispatchToProps = {
  loadPageData: fetchPageData,
};
MatchCentrePage.defaultProps = {
  image: null,
  loadingError: null,
  nextImg: null,
  prevImg: null,
};

MatchCentrePage.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  fixtureId: PropTypes.string.isRequired,
  image: PropTypes.shape({
    commentCount: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  isLoading: PropTypes.bool.isRequired,
  loadingError: LOADING_ERROR_PROP_TYPES,
  loadPageData: PropTypes.func.isRequired,
  nextImg: PropTypes.shape({
    commentCount: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  pageData: PropTypes.shape({
    album: PropTypes.shape(),
    report: PropTypes.shape(),
  }).isRequired,
  prevImg: PropTypes.shape({
    commentCount: PropTypes.number.isRequired,
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  previouslyLoaded: PropTypes.bool.isRequired,
  section: PropTypes.string.isRequired,
  teamId: PropTypes.number.isRequired,
  team: TEAM_PROP_TYPES.isRequired,
};

export default connect(mapStateToProps, dispatchToProps)(withTeam(MatchCentrePage));
